import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import { makeStyles } from '@mui/styles';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState, useRef, useCallback } from 'react';
import { chatApi } from 'src/api/chatApi';
import { projectApi } from 'src/api/projectApi';
import useAuth from 'src/hooks/useAuth';
import toast from 'react-hot-toast';
import { useAppDispatch } from 'src/hooks/useRedux';
import { reopenSession } from 'src/store/slices/chatWidgetSlice';
import { isWithinWorkingHoursOverlap, getNextWorkingHoursMessage, WorkingHours } from 'src/utils/workingHours';
import { ImagePreviewer } from '../widgets/modal/ImagePreviewer';
// import { Message } from 'src/types/chat';

const useStyles = makeStyles((theme) => ({
    attachmentContainer: {
        marginTop: theme.spacing(1),
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
    },
    attachmentChip: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
        },
    },
    attachmentIcon: {
        fontSize: 16,
        marginRight: theme.spacing(0.5),
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalContent: {
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        '&:focus': {
            outline: 'none',
        },
    },
    modalCloseButton: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
        zIndex: 1,
    },
    modalImage: {
        maxWidth: '100%',
        maxHeight: '80vh',
        objectFit: 'contain',
    },
}));

interface MessageBy {
    id: string;
    name: string;
}

interface Attachment {
    id: string;
    name: string;
    size: number;
    type: string;
    url: string;
}

interface Message {
    content: string;
    messageBy: MessageBy;
    isMessagedBySupportUser: boolean;
    createdAt: string;
    id: string;
    sessionId: string;
    readBy: any[];
    attachments?: Attachment[] | string[];
}

interface SessionData {
    sessionStatus: string;
    createdAt: string;
    id: string;
    orgId: string;
    customerId: string;
    projectId: string;
    supportUsers: any[];
    caseKey?: string;
}

interface ProjectData {
    id: string;
    name: string;
    description?: string;
    key?: string;
}

interface GroupedSessions {
    projectId: string;
    projectData: ProjectData | null;
    sessions: SessionData[];
    isExpanded: boolean;
}

export const ChatsHistory = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const [sessions, setSessions] = useState<SessionData[]>([]);
    const [groupedSessions, setGroupedSessions] = useState<GroupedSessions[]>([]);
    const [projectsMap, setProjectsMap] = useState<Map<string, ProjectData>>(new Map());
    const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [sessionActionLoading, setSessionActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [pageSize] = useState(20); // Number of messages per page
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    // Working hours state
    const [globalWorkingHours, setGlobalWorkingHours] = useState<WorkingHours | null>(null);
    const [workingHoursLoading, setWorkingHoursLoading] = useState<boolean>(true);
    const [isWithinWorkingHours, setIsWithinWorkingHours] = useState<boolean>(true);

    // Attachment modal state
    // const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
    // const [selectedAttachment, setSelectedAttachment] = useState<string | null>(null);

    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [fileDialogOpen, setFileDialogOpen] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState<string>('');

    // Fetch global working hours from API
    useEffect(() => {
        const fetchGlobalWorkingHours = async (retryCount = 0) => {
            try {
                setWorkingHoursLoading(true);
                const response = await chatApi.getGlobalWorkingHours();
                if (response && response.data) {
                    const workingHours: WorkingHours = {
                        startTime: response.data.startTime,
                        endTime: response.data.endTime,
                        timezone: response.data.timezone
                    };
                    setGlobalWorkingHours(workingHours);
                    setWorkingHoursLoading(false);
                    console.log('Global working hours loaded:', workingHours);
                } else {
                    throw new Error('No working hours data received from API');
                }
            } catch (e) {
                console.error('Error fetching global working hours:', e);
                // Retry once after 2 seconds if this is the first failure
                if (retryCount === 0) {
                    console.log('Retrying to fetch global working hours in 2 seconds...');
                    setTimeout(() => fetchGlobalWorkingHours(1), 2000);
                } else {
                    // After retry failure, use default values and show warning
                    console.warn('Using default working hours due to API failure');
                    toast.error('Unable to fetch current working hours. Using default schedule.');
                }
                // Keep globalWorkingHours as null to use default values
            } finally {
                // Set loading to false only after final attempt (success or final failure)
                if (retryCount > 0) {
                    setWorkingHoursLoading(false);
                }
            }
        };

        fetchGlobalWorkingHours();
    }, []);

    // Check working hours on component mount and set up periodic checks
    useEffect(() => {
        if (workingHoursLoading) return undefined; // Wait for working hours to be loaded

        const checkWorkingHours = () => {
            const withinHours = isWithinWorkingHoursOverlap(globalWorkingHours || undefined);
            setIsWithinWorkingHours(withinHours);
        };

        // Check immediately
        checkWorkingHours();

        // Check every minute
        const interval = setInterval(checkWorkingHours, 60000);

        return () => clearInterval(interval);
    }, [globalWorkingHours, workingHoursLoading]);

    // Handle Escape key to close image previewer
    useEffect((): (() => void) => {
        const handleEscape = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && previewImage) {
                setPreviewImage(null);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [previewImage]);

    // Helper function to check if attachment is an image
    const isImageAttachment = (attachment: string | Attachment): boolean => {
        const attachmentUrl = typeof attachment === 'string' ? attachment : attachment.url;
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
        const lowerAttachment = attachmentUrl.toLowerCase();
        return (
            imageExtensions.some((ext) => lowerAttachment.includes(ext)) ||
            lowerAttachment.includes('image/')
        );
    };

    // Handle image click to open previewer
    const handleImageClick = (imageUrl: string): void => {
        setPreviewImage(imageUrl);
    };

    // Handle file click to download
    const handleFileClick = (fileUrl: string): void => {
        setSelectedFileUrl(fileUrl);
        setFileDialogOpen(true);
    };

    // Handle file download dialog actions
    const handleDownloadFile = (): void => {
        window.open(selectedFileUrl, '_blank');
        setFileDialogOpen(false);
    };

    const handleCloseFileDialog = (): void => {
        setFileDialogOpen(false);
        setSelectedFileUrl('');
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch {
            return dateString;
        }
    };



    // Helper function to get file icon based on type
    // const getFileIcon = (fileType: string) => {
    //     if (fileType.startsWith('image/')) {
    //         return <ImageIcon className={classes.attachmentIcon} />;
    //     }
    //     if (fileType === 'application/pdf') {
    //         return <PictureAsPdfIcon className={classes.attachmentIcon} />;
    //     }
    //     if (fileType.includes('document') || fileType.includes('text')) {
    //         return <DescriptionIcon className={classes.attachmentIcon} />;
    //     }
    //     return <InsertDriveFileIcon className={classes.attachmentIcon} />;
    // };

    // // Helper function to download attachment
    // const downloadAttachment = (attachmentUrl: string, fileName: string) => {
    //     const link = document.createElement('a');
    //     link.href = attachmentUrl;
    //     link.download = fileName;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };

    // // Helper function to open attachment modal
    // const openAttachmentModal = (attachmentUrl: string) => {
    //     setSelectedAttachment(attachmentUrl);
    //     setIsAttachmentModalOpen(true);
    // };

    // // Helper function to close attachment modal
    // const closeAttachmentModal = () => {
    //     setIsAttachmentModalOpen(false);
    //     setSelectedAttachment(null);
    // };

    const fetchProjectDetails = async (projectId: string): Promise<ProjectData | null> => {
        try {
            const response = await projectApi.getProjectDetailsById(projectId);
            if (response && response.data) {
                return {
                    id: projectId,
                    name: response.data.name || `Project ${projectId.slice(-6)}`,
                    description: response.data.description,
                    key: response.data.key
                };
            }
        } catch (err) {
            console.error(`Error fetching project details for ${projectId}:`, err);
        }
        return {
            id: projectId,
            name: `Project ${projectId.slice(-6)}`,
            description: undefined,
            key: undefined
        };
    };

    const groupSessionsByProject = async (sessionsData: SessionData[]) => {
        setProjectsLoading(true);
        const uniqueProjectIds = Array.from(new Set(sessionsData.map(session => session.projectId)));
        const newProjectsMap = new Map<string, ProjectData>();

        // Fetch project details for each unique project ID
        const projectPromises = uniqueProjectIds.map(async (projectId) => {
            if (!projectsMap.has(projectId)) {
                const projectData = await fetchProjectDetails(projectId);
                if (projectData) {
                    newProjectsMap.set(projectId, projectData);
                }
            } else {
                newProjectsMap.set(projectId, projectsMap.get(projectId)!);
            }
        });

        await Promise.all(projectPromises);
        setProjectsMap(newProjectsMap);

        // Group sessions by project ID
        const grouped: GroupedSessions[] = uniqueProjectIds.map(projectId => ({
            projectId,
            projectData: newProjectsMap.get(projectId) || null,
            sessions: sessionsData.filter(session => session.projectId === projectId),
            isExpanded: true // Default to expanded
        }));

        // Sort by project name
        grouped.sort((a, b) => {
            const nameA = a.projectData?.name || `Project ${a.projectId.slice(-6)}`;
            const nameB = b.projectData?.name || `Project ${b.projectId.slice(-6)}`;
            return nameA.localeCompare(nameB);
        });

        setGroupedSessions(grouped);
        setProjectsLoading(false);
    };

    const toggleProjectExpansion = (projectId: string) => {
        setGroupedSessions(prev =>
            prev.map(group =>
                group.projectId === projectId
                    ? { ...group, isExpanded: !group.isExpanded }
                    : group
            )
        );
    };

    useEffect(() => {
        const fetchChatSessions = async () => {
            try {
                setLoading(true);
                const response = await chatApi.getSessionsByCustomerId(user.id);
                let sessionsData: SessionData[] = [];

                if (response && Array.isArray(response)) {
                    sessionsData = response;
                } else if (response && response.data && Array.isArray(response.data)) {
                    sessionsData = response.data;
                }

                setSessions(sessionsData);

                // Group sessions by project after fetching
                if (sessionsData.length > 0) {
                    await groupSessionsByProject(sessionsData);
                } else {
                    setGroupedSessions([]);
                }
            } catch (err) {
                setError('Failed to fetch chat sessions');
                console.error('Error fetching sessions:', err);
            } finally {
                setLoading(false);
            }
        };

        if (user?.id) {
            fetchChatSessions();
        }
    }, [user?.id]);

    const handleSessionClick = async (session: SessionData) => {
        setSelectedSession(session);
        setMessagesLoading(true);
        setMessages([]);
        setCurrentPage(0);
        setHasMoreMessages(true);

        try {
            const messagesResponse = await chatApi.getMessagesBySessionId(session.id, 0, pageSize, '1');
            if (messagesResponse && Array.isArray(messagesResponse)) {
                const newMessages = messagesResponse[0]?.messages || [];
                setMessages(newMessages);
                setHasMoreMessages(newMessages.length === pageSize);
            } else if (messagesResponse && messagesResponse.data && Array.isArray(messagesResponse.data)) {
                const newMessages = messagesResponse.data[0]?.messages || [];
                setMessages(newMessages);
                setHasMoreMessages(newMessages.length === pageSize);
            } else {
                setMessages([]);
                setHasMoreMessages(false);
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
            setMessages([]);
            setHasMoreMessages(false);
        } finally {
            setMessagesLoading(false);
        }
    };



    const formatChatForExport = (response: any, session: SessionData | null) => {
        let exportText = '';

        // Add header information
        exportText += `CHAT EXPORT\n`;
        exportText += `=============\n\n`;

        if (session) {
            exportText += `Session ID: ${session.id}\n`;
            exportText += `Case Key: ${session.caseKey || 'N/A'}\n`;
            exportText += `Status: ${session.sessionStatus}\n`;
            exportText += `Created: ${formatDate(session.createdAt)}\n`;
            exportText += `Organization ID: ${session.orgId}\n`;
            exportText += `Customer ID: ${session.customerId}\n`;
            exportText += `Project ID: ${session.projectId}\n\n`;
        }

        exportText += `MESSAGES\n`;
        exportText += `=========\n\n`;

        // Check if response is a plain text string (which seems to be the case)
        if (typeof response === 'string') {
            // The response is already formatted text, just append it
            exportText += response;
        }
        else {
            exportText += 'No messages found in this session.\n';
        }

        return exportText;
    }

    const downloadTextFile = (content: string, filename: string) => {
        // Create a blob with the text content
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary anchor element and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        // Add to DOM, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL
        window.URL.revokeObjectURL(url);
    }


    const handleExportChat = async (sessionId: string, caseKey: string) => {
        try {
            const response = await chatApi.exportChat(sessionId);

            // Convert the response to formatted text
            const formattedText = formatChatForExport(response, selectedSession);
            console.log('formattedText', formattedText);
            // Create and download the text file
            downloadTextFile(formattedText, `chat-export-${caseKey}.txt`);

            toast.success('Chat exported successfully');
        } catch (e) {
            console.error('Error exporting chat:', e);
            toast.error('Failed to export chat');
        }
    }

    const handleReOpenSession = async (sessionId: string) => {
        // Check working hours before allowing session to be reopened
        if (!isWithinWorkingHours) {
            const message = getNextWorkingHoursMessage(globalWorkingHours || undefined);
            toast.error(`Cannot reopen session outside working hours. ${message}`);
            return;
        }

        setSessionActionLoading(true);
        try {
            const response = await chatApi.updateSessionStatus(sessionId, 'OPEN');
            console.log('response', response);

            // Update the session status in the local state
            setSessions(prevSessions =>
                prevSessions.map(session =>
                    session.id === sessionId
                        ? { ...session, sessionStatus: 'OPEN' }
                        : session
                )
            );

            // Update the session status in grouped sessions
            setGroupedSessions(prevGrouped =>
                prevGrouped.map(group => ({
                    ...group,
                    sessions: group.sessions.map(session =>
                        session.id === sessionId
                            ? { ...session, sessionStatus: 'OPEN' }
                            : session
                    )
                }))
            );

            // Update selected session if it's the one being reopened
            if (selectedSession?.id === sessionId) {
                setSelectedSession(prev => prev ? { ...prev, sessionStatus: 'OPEN' } : null);
            }

            // Find the session data to pass to Redux
            const sessionData = sessions.find(session => session.id === sessionId);

            // Dispatch Redux action to open chat widget with this session
            dispatch(reopenSession({ sessionId, sessionData }));

            toast.success('Session re-opened successfully');
        }
        catch (err) {
            console.error('Error re-opening session:', err);
            toast.error('Failed to re-open session');
        }
        finally {
            setSessionActionLoading(false);
        }
    }

    const loadMoreMessages = useCallback(async () => {
        if (!selectedSession || loadingMoreMessages || !hasMoreMessages) {
            return;
        }

        setLoadingMoreMessages(true);
        const nextPage = currentPage + 1;

        try {
            const messagesResponse = await chatApi.getMessagesBySessionId(selectedSession.id, nextPage, pageSize, '1');
            let newMessages: Message[] = [];

            if (messagesResponse && Array.isArray(messagesResponse)) {
                newMessages = messagesResponse[0]?.messages || [];
            } else if (messagesResponse && messagesResponse.data && Array.isArray(messagesResponse.data)) {
                newMessages = messagesResponse.data[0]?.messages || [];
            }

            if (newMessages.length > 0) {
                setMessages(prevMessages => [...prevMessages, ...newMessages]);
                setCurrentPage(nextPage);
                setHasMoreMessages(newMessages.length === pageSize);
            } else {
                setHasMoreMessages(false);
            }
        } catch (err) {
            console.error('Error loading more messages:', err);
            setHasMoreMessages(false);
        } finally {
            setLoadingMoreMessages(false);
        }
    }, [selectedSession, loadingMoreMessages, hasMoreMessages, currentPage, pageSize]);

    const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

        if (isNearBottom && hasMoreMessages && !loadingMoreMessages && !messagesLoading) {
            loadMoreMessages();
        }
    }, [hasMoreMessages, loadingMoreMessages, messagesLoading, loadMoreMessages]);


    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'open':
                return 'primary';
            case 'close':
            case 'closed':
                return 'default';
            case 'pending':
                return 'secondary';
            case 'active':
                return 'primary';
            default:
                return 'default';
        }
    };

    const sessionStatusLower = selectedSession?.sessionStatus?.toLowerCase();
    const isSessionClosed = sessionStatusLower === 'close' || sessionStatusLower === 'closed';

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box padding={3} height="calc(100vh - 100px)">
            <Typography fontWeight={600} fontSize={22} variant="h4" gutterBottom>
                Chat History
            </Typography>

            {error && (
                <Alert severity="error" style={{ marginBottom: 16 }}>
                    {error}
                </Alert>
            )}


            <Box display="flex" height="100%" gap={2}>
                {/* Left Side - Sessions List (30%) */}
                <Paper elevation={2} style={{ width: '30%', overflow: 'hidden' }}>
                    <Box padding={2}>
                        <Typography variant="h6" gutterBottom>
                            Sessions ({sessions.length})
                            {projectsLoading && (
                                <CircularProgress size={16} style={{ marginLeft: 8 }} />
                            )}
                        </Typography>
                        <Divider />
                    </Box>

                    <Box style={{ height: 'calc(100% - 80px)', overflow: 'auto' }}>
                        {groupedSessions.length === 0 ? (
                            <Box padding={2}>
                                <Typography color="textSecondary" align="center">
                                    {loading ? 'Loading sessions...' : 'No chat sessions found'}
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {groupedSessions.map((group) => (
                                    <Box key={group.projectId}>
                                        {/* Project Header */}
                                        <ListItemButton
                                            onClick={() => toggleProjectExpansion(group.projectId)}
                                            style={{
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                borderBottom: '1px solid #e0e0e0',
                                                paddingLeft: 18,
                                                paddingRight: 8
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" >
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {group.projectData?.name || `Project ${group.projectId.slice(-6)}`}
                                                        </Typography>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Chip
                                                                label={`${group.sessions.length} session${group.sessions.length !== 1 ? 's' : ''}`}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                            <IconButton size="small">
                                                                {group.isExpanded ? <ExpandLess /> : <ExpandMore />}
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                }

                                            />
                                        </ListItemButton>

                                        {/* Sessions List */}
                                        <Collapse in={group.isExpanded} timeout="auto" unmountOnExit>
                                            <List disablePadding>
                                                {group.sessions.map((session) => (
                                                    <ListItemButton
                                                        key={session.id}
                                                        onClick={() => handleSessionClick(session)}
                                                        selected={selectedSession?.id === session.id}
                                                        style={{
                                                            borderBottom: '1px solid #f0f0f0',
                                                            backgroundColor: selectedSession?.id === session.id ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                                                            paddingLeft: 24
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                    <Typography variant="body2" noWrap>
                                                                        {session.caseKey || `Session ${session.id.slice(-6)}`}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={session.sessionStatus}
                                                                        size="small"
                                                                        color={getStatusColor(session.sessionStatus) as any}
                                                                    />
                                                                </Box>
                                                            }
                                                            secondary={
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {formatDate(session.createdAt)}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </Collapse>
                                    </Box>
                                ))}
                            </List>
                        )}
                    </Box>
                </Paper>

                {/* Right Side - Selected Session Data (70%) */}
                <Paper elevation={2} style={{ width: '70%', overflow: 'hidden', }}>
                    {selectedSession ? (
                        <Box height="100%" display="flex" flexDirection="column" >
                            {/* Session Details Header */}
                            <Box padding={2} borderBottom="1px solid #e0e0e0">
                                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                                    {/* Left side */}
                                    <Box>
                                        <Typography variant="h6" style={{ marginBottom: 1 }}>
                                            {selectedSession.caseKey ? `Case: ${selectedSession.caseKey}` : 'Session Details'}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 1 }}>
                                            Session ID: {selectedSession.id}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Created: {formatDate(selectedSession.createdAt)}
                                        </Typography>
                                    </Box>

                                    {/* Right side */}
                                    <Box>
                                        <Box display="flex" flexDirection="column" alignItems="flex-end" >
                                            {/* Status line */}
                                            <Box display="flex" alignItems="center" gap={1} style={{ marginBottom: 3 }}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Status:
                                                </Typography>
                                                <Chip
                                                    label={selectedSession.sessionStatus}
                                                    size="small"
                                                    color={getStatusColor(selectedSession.sessionStatus) as any}
                                                />
                                            </Box>

                                            {/* Buttons line */}
                                            <Box display="flex" gap={1}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleReOpenSession(selectedSession.id)}
                                                    disabled={sessionActionLoading || selectedSession.sessionStatus === 'OPEN' || !isWithinWorkingHours}
                                                    title={!isWithinWorkingHours ? 'Support is currently outside working hours' : ''}
                                                >
                                                    {sessionActionLoading ? <CircularProgress size={16} /> : 'Re-open Chat'}
                                                </Button>

                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => { handleExportChat(selectedSession.id, selectedSession.caseKey) }}
                                                >
                                                    {sessionActionLoading ? <CircularProgress size={16} /> : 'Export Chat'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Optional alert */}
                                {!isWithinWorkingHours && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                        Support is currently outside working hours. You cannot reopen chat sessions until support is available.
                                    </Alert>
                                )}
                            </Box>



                            {/* Messages Area */}
                            <Box
                                flex={1}
                                padding={2}
                                overflow="auto"
                                onScroll={handleScroll}
                                ref={messagesContainerRef}
                                style={{
                                    backgroundColor: '#f5f5f5',
                                }}
                            >
                                <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
                                    Messages {messages.length > 0 && `(${messages.length})`}
                                </Typography>

                                {messagesLoading ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                                        <CircularProgress />
                                    </Box>
                                ) : messages.length > 0 ? (
                                    <Box>
                                        {messages.map((message) => (
                                            <Box
                                                key={message.id}
                                                style={{
                                                    display: 'flex',
                                                    marginBottom: 2,
                                                    alignItems: 'flex-start',
                                                    flexDirection: message.isMessagedBySupportUser ? 'row-reverse' : 'row',
                                                    gap: 1,
                                                }}
                                            >
                                                {/* Message Bubble */}
                                                <Box
                                                    style={{
                                                        backgroundColor: !message.isMessagedBySupportUser
                                                            ? theme.palette.primary.main
                                                            : theme.palette.grey[200],
                                                        color: !message.isMessagedBySupportUser
                                                            ? theme.palette.primary.contrastText
                                                            : theme.palette.text.primary,
                                                        borderRadius: 2,
                                                        padding: '12px 16px',
                                                        maxWidth: '70%',
                                                        wordBreak: 'break-word',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    {/* Sender name and badge */}
                                                    <Box
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginBottom: 0.5,
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            style={{
                                                                fontWeight: 600,
                                                                opacity: 0.8,
                                                                color: !message.isMessagedBySupportUser
                                                                    ? theme.palette.primary.contrastText
                                                                    : theme.palette.text.secondary,
                                                            }}
                                                        >
                                                            {message.messageBy.name}
                                                        </Typography>
                                                        {message.isMessagedBySupportUser && (
                                                            <Chip
                                                                label="Support"
                                                                size="small"
                                                                sx={{
                                                                    height: 16,
                                                                    fontSize: '0.65rem',
                                                                    backgroundColor: alpha(theme.palette.primary.contrastText, 0.2),
                                                                    color: !message.isMessagedBySupportUser
                                                                        ? theme.palette.primary.contrastText
                                                                        : theme.palette.text.secondary,
                                                                    '& .MuiChip-label': {
                                                                        paddingX: 1,
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    </Box>

                                                    {/* Message content */}
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            whiteSpace: 'pre-wrap',
                                                            lineHeight: 1.4,
                                                            marginBottom: 0.5,
                                                        }}
                                                    >
                                                        {message.content}
                                                    </Typography>

                                                    {/* Display attachments */}
                                                    {message.attachments && message.attachments.length > 0 && (
                                                        <Box
                                                            display="flex"
                                                            flexDirection="row"
                                                            flexWrap="wrap"
                                                            gap={1}
                                                            sx={{ mb: 1 }}
                                                        >
                                                            {message.attachments.map((attachment, index) => {
                                                                const attachmentUrl = typeof attachment === 'string' ? attachment : attachment.url;
                                                                const attachmentId = typeof attachment === 'string' ? `url_${index}` : attachment.id;

                                                                return isImageAttachment(attachment) ? (
                                                                    <img
                                                                        key={attachmentId}
                                                                        src={attachmentUrl}
                                                                        alt="Attachment"
                                                                        role="presentation"
                                                                        style={{
                                                                            width: '100px',
                                                                            height: '100px',
                                                                            borderRadius: '8px',
                                                                            cursor: 'pointer',
                                                                            objectFit: 'cover',
                                                                        }}
                                                                        onClick={() => handleImageClick(attachmentUrl)}
                                                                    />
                                                                ) : (
                                                                    <Box
                                                                        key={attachmentId}
                                                                        sx={{
                                                                            width: '100px',
                                                                            height: '100px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            border: '1px solid',
                                                                            borderColor: 'grey.300',
                                                                            borderRadius: 1,
                                                                            bgcolor: 'grey.50',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                        onClick={() => handleFileClick(attachmentUrl)}
                                                                    >
                                                                        <InsertDriveFileIcon
                                                                            style={{
                                                                                fontSize: 50,
                                                                                color: 'grey.600',
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                    {/* Timestamp */}
                                                    <Typography
                                                        variant="caption"
                                                        style={{
                                                            fontSize: '0.7rem',
                                                            opacity: 0.7,
                                                            display: 'block',
                                                            textAlign: 'right',
                                                            marginTop: 0.5,
                                                            color: !message.isMessagedBySupportUser
                                                                ? theme.palette.primary.contrastText
                                                                : theme.palette.text.secondary,
                                                        }}
                                                    >
                                                        {formatDate(message.createdAt)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}

                                        {/* Loading indicator for more messages */}
                                        {loadingMoreMessages && (
                                            <Box display="flex" justifyContent="center" alignItems="center" padding={2}>
                                                <CircularProgress size={20} />
                                                <Typography variant="caption" style={{ marginLeft: 8 }}>
                                                    Loading more messages...
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* End of messages indicator */}
                                        {!hasMoreMessages && messages.length > pageSize && (
                                            <Box display="flex" justifyContent="center" alignItems="center" padding={2}>
                                                <Typography variant="caption" color="textSecondary">
                                                    No more messages to load
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography color="textSecondary" align="center">
                                        No messages in this session
                                    </Typography>
                                )}
                                {isSessionClosed && !messagesLoading && (
                                    <p style={{ color: '#797979', fontSize: '14px', fontWeight: 500, marginTop: '10px', fontStyle: 'italic', textAlign: 'center' }}>
                                        This conversation has ended.
                                    </p>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                            flexDirection="column"
                        >
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                                Select a Session
                            </Typography>
                            <Typography color="textSecondary" align="center">
                                Choose a session from the left panel to view its details and messages
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Image Previewer Modal */}
            {previewImage && (
                <>
                    {/* Backdrop */}
                    <Box
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1400,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        }}
                        onClick={() => setPreviewImage(null)}
                    />

                    {/* Modal Content */}
                    <Box
                        sx={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1401,
                            width: '65vw',
                            height: '90vh',
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: theme.shadows[24],
                            p: 3,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box style={{ width: '100%', height: '100%' }}>
                            <ImagePreviewer
                                imageUrl={previewImage}
                                onClose={() => setPreviewImage(null)}
                            />
                        </Box>
                    </Box>
                </>
            )}

            {/* File Preview Dialog */}
            <Modal
                open={fileDialogOpen}
                onClose={handleCloseFileDialog}
                className={classes.modal}
                BackdropProps={{
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                }}
            >
                <Box className={classes.modalContent}>
                    <IconButton
                        className={classes.modalCloseButton}
                        onClick={handleCloseFileDialog}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box textAlign="center" padding={3}>
                        <InsertDriveFileIcon style={{ fontSize: 64, marginBottom: 16 }} />
                        <Typography variant="h6" gutterBottom>
                            File Cannot Be Previewed
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            This file cannot be previewed. Would you like to open it in a new tab?
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDownloadFile}
                            style={{ marginTop: 16, marginRight: 8 }}
                        >
                            Open in New Tab
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleCloseFileDialog}
                            style={{ marginTop: 16 }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
